/**
 * The page an ad lands on: one county, what we hold for it, and one button.
 *
 * Nothing here is written per county. The headline figures come off
 * `/api/status?county=` and the registry entry off `/api/counties`, the same
 * two public endpoints the coverage map reads, so the page says for Broward
 * exactly what the dashboard would — including the caveats: which signals the
 * county publishes, whether the score can rank, how old the feeds are. An ad
 * that promises what the county cannot deliver is a refund, and this page is
 * where the promise is made.
 *
 * Two audiences share it. A visitor from an ad gets the county, the numbers,
 * and the free tier's offer; a signed-in subscriber who follows a shared link
 * gets the same page with the button pointing at their dashboard instead.
 *
 * A slug that names a county we do not hold — or no county at all — falls
 * back to the statewide page rather than a 404: the visitor asked about
 * Florida and the answer to "do you have my county?" is the map, one click
 * below.
 */

import { useEffect, useMemo, useState } from "react";

import { api } from "../api/client";
import type { County, Plan, Status } from "../api/types";
import { useAuth } from "../auth/AuthProvider";
import { trialShort } from "../lib/billing";
import { countyName } from "../lib/counties";
import { countyDepth } from "../lib/countyDepth";
import { fmt, timestamp } from "../lib/format";
import { headline, lede } from "../lib/headline";
import { landingMetrics } from "../lib/landingMetrics";
import { fipsForSlug, signupPath } from "../lib/landing";
import { COVERAGE_PATH, DASHBOARD_PATH, SIGNIN_PATH } from "../lib/route";
import { scoreScope } from "../lib/scoreScope";
import { hasSourceWarning, sourceWarning } from "../lib/sources";
import { Link } from "../router";
import { Footer } from "./Footer";
import { coveredCounties } from "./Hero";

export function LandingPage({ slug }: { slug: string }) {
  const { user } = useAuth();
  const [counties, setCounties] = useState<County[]>([]);
  const [status, setStatus] = useState<Status | null>(null);
  // The prices *and* the blurbs, off `/api/plans`. The tier cards below used
  // to carry their own paragraph, and it promised a CSV export two days after
  // reports replaced it and "move whenever you like" against a thirty-day
  // switch — one click from a pricing page saying the opposite. The served
  // blurb is the one the pricing page renders, so the two cannot disagree.
  const [plans, setPlans] = useState<{ county: Plan; state: Plan } | null>(null);

  const fips = fipsForSlug(slug);
  const entry = counties.find((county) => county.fips === fips);
  // Live means the registry has data behind it. A county that is planned or
  // unwired is answered by the statewide page, whose map says so.
  const live = Boolean(entry && entry.status === "live");
  const scope = live ? fips : "";

  useEffect(() => {
    const controller = new AbortController();
    api
      .counties(controller.signal)
      .then((response) => setCounties(response.counties))
      .catch(() => {});
    api
      .plans(controller.signal)
      .then((response) => {
        const county = response.plans.find((plan) => plan.id === "county");
        const state = response.plans.find((plan) => plan.id === "state");
        if (county && state) setPlans({ county, state });
      })
      .catch(() => {});
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    api.status(scope, controller.signal).then(setStatus).catch(() => {});
    return () => controller.abort();
  }, [scope]);

  const liveCounties = useMemo(
    () => counties.filter((county) => county.status === "live"),
    [counties],
  );
  const name = live ? countyName(fips) : "";
  const where = name ? `${name} County` : "Florida";
  const stats = status?.stats;
  const rank = scoreScope(entry?.signals);
  const depth = entry ? countyDepth(entry.signals) : null;
  const lastPull = timestamp(status?.finished_at);
  const title = headline(where, stats, entry?.signals);

  // Where the button goes. A subscriber does not need another account.
  const target = user
    ? scope
      ? `${DASHBOARD_PATH}?county=${scope}`
      : DASHBOARD_PATH
    : signupPath(scope);

  return (
    <>
      <header className="topbar">
        <Link to="/" className="wordmark">
          <span>D</span>is<span>TR</span>ess Signal
        </Link>
        <div className="loc hide-sm">{where}</div>
        <div className="spacer" />
        <Link to={user ? DASHBOARD_PATH : SIGNIN_PATH} className="linkbtn">
          {user ? "Dashboard" : "Sign in"}
        </Link>
      </header>

      <main className="landing">
        <div className="eyebrow">
          {name ? `${name} County, Florida` : `Florida · ${liveCounties.length || "…"} counties`}
          {lastPull ? ` · records pulled ${lastPull}` : ""}
        </div>

        {/* Both derived from the county's signal list — see lib/headline.ts.
            This is the page an ad lands on, and these two sentences are read
            before the caveats below them, so they may only promise what the
            county publishes: no "open case" in a county with no case feed,
            no liens where no lien register is read. */}
        <h1 className="landing-title">
          {title.before}
          <em>{title.emphasis}</em>
          {title.after}
        </h1>

        <p className="landing-lede">
          {lede(name ? `${name} County's` : "each county's", stats, entry?.signals)}
        </p>

        <div className="landing-action">
          <Link to={target} className="btn">
            {user
              ? `Open ${where}`
              : `See the ten worst buildings in ${where} — free`}
          </Link>
          <span className="sub">
            {user
              ? "You're signed in."
              : "Free account. No card. The ten highest-scoring buildings, with address and milestone standing."}
          </span>
        </div>

        <div className="metastrip">
          {landingMetrics(stats, entry?.signals).map((metric) => (
            <Metric key={metric.label} label={metric.label} value={fmt(metric.value)} />
          ))}
        </div>

        {/* The same three honesty lines the dashboard shows, in the same
            words. They are here *because* this is the page an ad lands on:
            a county with no case feed has to say so before anyone pays for
            it, not after. */}
        {depth && <p className="hero-caveat">{depth.summary}</p>}
        {rank.note && <p className="hero-caveat">{rank.note}</p>}
        {status && hasSourceWarning(status.sources) && (
          <p className="hero-caveat sources-caveat">
            {sourceWarning(status.sources)}
          </p>
        )}

        <section className="landing-tiers">
          <div>
            <div className="eyebrow">Free</div>
            <h2>The ten worst</h2>
            <p>
              The ten highest-scoring buildings in {where}: address, score,
              milestone standing and what the score is built from. Enough to
              know whether the county is worth your time.
            </p>
          </div>
          <div>
            <div className="eyebrow">
              County{plans ? ` · $${plans.county.price_usd}/mo` : ""}
              {plans && trialShort(plans.county.trial_days)
                ? ` · ${trialShort(plans.county.trial_days)}` : ""}
            </div>
            <h2>Every building and parcel</h2>
            <p>{plans?.county.blurb ?? ""}</p>
          </div>
          <div>
            <div className="eyebrow">
              State{plans ? ` · $${plans.state.price_usd}/mo` : ""}
            </div>
            <h2>
              {liveCounties.length ? `All ${liveCounties.length} counties` : "Every county"}
            </h2>
            <p>
              {coveredCounties(counties)}
              {liveCounties.length ? " — and every county added later." : "."}
            </p>
          </div>
        </section>

        <p className="landing-foot sub">
          Public record, read daily and scored; not a valuation, an inspection,
          or advice. Details on the{" "}
          <Link to={COVERAGE_PATH}>coverage map</Link>, which breaks every county
          down by municipality and feed.
        </p>
      </main>

      <Footer agreementVersion={user?.legal.current_version} />
    </>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="eyebrow">{label}</span>
      <b>{value}</b>
    </div>
  );
}
